import * as React from 'react';
import { Responsive, TInputSize } from '@cian/ui-kit';
import { debounce } from 'throttle-debounce';
import { RegionSuggestDesktop } from './components/RegionSuggestDesktop';
import { RegionSuggestMobile } from './components/RegionSuggestMobile';
import { DEFAULT_INPUT_DELAY } from '../../constants/timeouts';
import { IRegion } from '../../types/requestForm';

export interface IRegionSuggestProps {
  disabled?: boolean;
  name?: string;
  onChange(region: IRegion | null): void;
  onRegionSearch(text: string): Promise<IRegion[]>;
  placeholder?: string;
  size?: TInputSize;
  value: IRegion | null;
  invalid?: boolean;
}

export const RegionSuggest: React.FC<IRegionSuggestProps> = ({ onChange, onRegionSearch, value, ...inputProps }) => {
  const [regionName, setRegionName] = React.useState('');
  const [suggests, setSuggests] = React.useState<IRegion[]>([]);

  const innerValueRef = React.useRef<IRegion | null>(null);

  const changeRegion = React.useCallback(
    (region: IRegion | null) => {
      innerValueRef.current = region;
      onChange(region);
    },
    [onChange],
  );

  const selectRegion = React.useCallback(
    (region: IRegion) => {
      changeRegion(region);
      setRegionName(region.label);
    },
    [changeRegion],
  );

  const handleRegionNameChange = React.useCallback(
    (name: string) => {
      setRegionName(name);
      changeRegion(null);
    },
    [changeRegion],
  );

  // oxlint-disable-next-line react/exhaustive-deps
  const fetchSuggests = React.useCallback(
    debounce(DEFAULT_INPUT_DELAY, async (text: string) => {
      const result = await onRegionSearch(text);
      setSuggests(result);
    }),
    [onRegionSearch],
  );

  React.useEffect(() => {
    if (innerValueRef.current !== value) {
      setRegionName(value?.label || '');
      innerValueRef.current = value;
    }
  }, [value]);

  React.useEffect(() => {
    fetchSuggests(regionName);
    // oxlint-disable-line react/exhaustive-deps
  }, [regionName]);

  React.useEffect(() => {
    return () => {
      fetchSuggests.cancel();
    };
    // oxlint-disable-line react/exhaustive-deps
  }, []);

  return (
    <>
      <Responsive phone tablet>
        <RegionSuggestMobile
          {...inputProps}
          onInputChange={handleRegionNameChange}
          onSelect={selectRegion}
          region={value}
          suggests={suggests}
          value={regionName}
        />
      </Responsive>

      <Responsive desktop>
        <RegionSuggestDesktop
          {...inputProps}
          onInputChange={handleRegionNameChange}
          onSelect={selectRegion}
          suggests={suggests}
          value={regionName}
        />
      </Responsive>
    </>
  );
};
